// StudentDashboard.jsx - النسخة الكاملة مع جميع الميزات وتصميم Vertical List النظيف
import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/common/DashboardLayout';
import { CoursePurchaseModal } from '../../components/payment/CoursePurchaseModal';
import { LecturePlayer } from '../../components/lectures/LecturePlayer';
import { QuizPlayer } from '../../components/quizzes/QuizPlayer';
import {
  LayoutDashboard,
  BookOpen,
  PlayCircle,
  ClipboardList,
  RefreshCw,
  MessageSquare,
  ShoppingCart,
  Gift,
  CheckCircle,
  Clock,
  Award,
  TrendingUp,
  Video,
  VideoOff,
  Lock,
  ArrowRight,
  Search,
  AlertCircle,
  FileText,
  File,
  ExternalLink,
  Download,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  GraduationCap,
  Sparkles,
  FileQuestion,
  Link2,
  BookMarked,
  X,
  AlertTriangle,
  Film,
  Bell,
  Ban,
  Trash2,
  Star,
  Eye
} from 'lucide-react';
import * as d1Api from '../../services/d1Api';

export const StudentDashboard = () => {
  const { user, profile: userData, signOut: logout } = useAuth();
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [myCourses, setMyCourses] = useState([]);
  const [suggestedCourses, setSuggestedCourses] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedLecture, setSelectedLecture] = useState(null);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [centerCode, setCenterCode] = useState('');
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [codeSuccess, setCodeSuccess] = useState('');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  
  const [completedLectures, setCompletedLectures] = useState([]);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showQuizConfirm, setShowQuizConfirm] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizQuestionsLoading, setQuizQuestionsLoading] = useState(false);
  const [quizReadOnly, setQuizReadOnly] = useState(false);
  const [quizPreviousAnswers, setQuizPreviousAnswers] = useState({});
  const [quizPreviousScore, setQuizPreviousScore] = useState(0);
  const [myAttempts, setMyAttempts] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [lectureAssignments, setLectureAssignments] = useState({});
  const [forumPosts, setForumPosts] = useState([]);
  const [forumContent, setForumContent] = useState('');
  const [forumLoading, setForumLoading] = useState(false);
  
  const [currentVideoPart, setCurrentVideoPart] = useState(0);
  const [forceResetKey, setForceResetKey] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardFilter, setLeaderboardFilter] = useState('');
  
  // Fix 2: Messages state
  const [messages, setMessages] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Fix 3: Forum block state
  const [isBlockedFromForum, setIsBlockedFromForum] = useState(false);
  const [myForumPosts, setMyForumPosts] = useState([]);
  
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  
  // Forum rate limiting state
  const FORUM_DAILY_LIMIT = 5;
  const [forumCooldownEnds, setForumCooldownEnds] = useState(null);

  // ====== ACTIVE HEARTBEAT ======
  // Every 15 seconds, make an authenticated API call to check if session is still valid.
  // If Device B logged in, Device A's session was deleted → heartbeat hits 401 → d1Api interceptor wipes localStorage → redirect to /login.
  useEffect(() => {
    if (!user?.id) return;

    const heartbeat = setInterval(async () => {
      try {
        await d1Api.getProfile(user.id);
      } catch (err) {
        // d1Api interceptor already handles 401 → localStorage.clear() + redirect
        console.warn('Heartbeat detected session issue:', err);
      }
    }, 15000); // Every 15 seconds

    return () => clearInterval(heartbeat);
  }, [user?.id]);

  // Separate active and expired courses based on the is_expired flag from the backend
  const activeCourses = myCourses.filter(c => !c.is_expired);
  const expiredCourses = myCourses.filter(c => c.is_expired);
  const enrolledCourseIds = activeCourses.map(c => c.id);

  // Show ALL lectures — no filtering by enrollment/preview.
  // Only filter by selectedCourse if user picked one.
  const availableLectures = lectures.filter(lec => {
    if (selectedCourse) {
      return lec.course_id === selectedCourse.id;
    }
    return true;
  });

  const pdfs = useMemo(() => {
    try {
      const list = JSON.parse(selectedLecture?.pdfs_list || "[]");
      if (list.length > 0) return list;
    } catch (e) {
    }
    return selectedLecture?.pdf_url ? [{ title: "مذكرة الشرح", url: selectedLecture.pdf_url }] : [];
  }, [selectedLecture]);

  const lectureQuizzes = useMemo(() => {
    if (!selectedLecture) return [];
    let qIds = [];
    if (selectedLecture.quiz_ids) {
      try { qIds = JSON.parse(selectedLecture.quiz_ids); } catch(e) {}
    }
    if (selectedLecture.quiz_id && !qIds.includes(selectedLecture.quiz_id)) {
      qIds.push(selectedLecture.quiz_id);
    }
    return qIds.map(id => quizzes.find(q => q.id === id) || { id, title: 'اختبار المحاضرة' });
  }, [selectedLecture, quizzes]);

  const getVideoParts = (lecture) => {
    if (!lecture) return [];
    try {
      if (typeof lecture.videos_list === 'string') {
        const parsed = JSON.parse(lecture.videos_list);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } else if (Array.isArray(lecture.videos_list) && lecture.videos_list.length > 0) {
        return lecture.videos_list;
      }
    } catch (e) {
      console.warn("Error parsing videos_list", e);
    }
    if (lecture.youtube_url || lecture.youtube_id) {
      return [{ title: 'الجزء الرئيسي', url: lecture.youtube_url || lecture.youtube_id }];
    }
    return [];
  };

  const videoParts = getVideoParts(selectedLecture);
  const hasMultipleParts = videoParts.length > 1;
  const isLastPart = currentVideoPart === videoParts.length - 1;

  useEffect(() => {
    document.body.classList.toggle('light-mode', !isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      setDataLoading(true);
      try {
        const enrolledRes = await d1Api.getEnrolledCourses(user.id);
        if (enrolledRes.success) {
          setMyCourses(enrolledRes.data || []);
        }

        const exactGrade = typeof userData?.grade === 'string' ? userData.grade.trim() : null;
        const suggestedRes = await d1Api.getSuggestedCourses(exactGrade || null);
        if (suggestedRes.success) setSuggestedCourses(suggestedRes.data || []);

        const lecturesRes = await d1Api.getLectures();
        if (lecturesRes.success) {
          const allLecs = lecturesRes.data || [];
          if (exactGrade && suggestedRes.success && enrolledRes.success) {
            const validCourseIds = new Set([
              ...(enrolledRes.data || []).map(c => c.id),
              ...(suggestedRes.data || []).map(c => c.id)
            ]);
            setLectures(allLecs.filter(lec => validCourseIds.has(lec.course_id)));
          } else {
            setLectures(allLecs);
          }
        }

        if (selectedCourse?.id) {
          const quizzesRes = await d1Api.getQuizzesByCourse(selectedCourse.id);
          if (quizzesRes.success) {
            setQuizzes(quizzesRes.data || []);
          }
        } else {
          const allQuizzesRes = await d1Api.getQuizzesByCourse();
          if (allQuizzesRes.success) {
            const allQz = allQuizzesRes.data || [];
            if (exactGrade && suggestedRes.success && enrolledRes.success) {
              const validCourseIds = new Set([
                ...(enrolledRes.data || []).map(c => c.id),
                ...(suggestedRes.data || []).map(c => c.id)
              ]);
              setQuizzes(allQz.filter(q => validCourseIds.has(q.course_id)));
            } else {
              setQuizzes(allQz);
            }
          }
        }

        if (user?.id) {
          const wrongAnswersRes = await d1Api.getWrongAnswers(user.id);
          if (wrongAnswersRes.success) setWrongAnswers(wrongAnswersRes.data || []);
          
          const completedRes = await d1Api.getCompletedLectures(user.id);
          if (completedRes.success && completedRes.data) {
            setCompletedLectures(completedRes.data.map(l => l.lecture_id));
          }

          // Fix 2: Fetch messages
          const messagesRes = await d1Api.getStudentMessages(user.id);
          if (messagesRes.success) {
            setMessages(messagesRes.data || []);
            setUnreadMessages((messagesRes.data || []).length);
          }

          // Fix 3: Check forum block status
          const profileRes = await d1Api.getProfile(user.id);
          if (profileRes.success && profileRes.data) {
            setIsBlockedFromForum(profileRes.data.is_blocked_from_forum === 1);
          }

          // Fix 3: Fetch own forum posts (including pending)
          const forumRes = await d1Api.getForumPostsForUser(user.id);
          if (forumRes.success) {
            setMyForumPosts(forumRes.data || []);
          }

          // Fetch announcements
          const announcementsRes = await d1Api.getAnnouncements();
          if (announcementsRes.success) {
            setAnnouncements(announcementsRes.data || []);
          }

          // Fetch quiz attempts
          const attemptsRes = await d1Api.getMyQuizAttempts(user.id);
          if (attemptsRes.success) {
            setMyAttempts(attemptsRes.data || []);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();

    // Fetch leaderboard in parallel - default to user's grade
    const exactGrade = typeof userData?.grade === 'string' ? userData.grade.trim() : null;
    d1Api.getLeaderboard(exactGrade).then(res => { if (res.success) setLeaderboard(res.data || []); });
    if (exactGrade) setLeaderboardFilter(exactGrade);
  }, [user, selectedCourse?.id]);

  useEffect(() => {
    let cancelled = false;
    const fetchLecturesForCourse = async () => {
      if (!selectedCourse?.id) return;
      const res = await d1Api.getLectures(selectedCourse.id);
      if (!cancelled && res?.success) {
        setLectures(res.data || []);
      }
    };
    fetchLecturesForCourse();
    return () => { cancelled = true; };
  }, [selectedCourse?.id]);

  useEffect(() => {
    setCurrentVideoPart(0);
  }, [selectedLecture]);

  useEffect(() => {
    if (activeTab === 'mistakes' && user?.id) {
      const fetchMistakes = async () => {
        const wrongAnswersRes = await d1Api.getWrongAnswers(user.id);
        if (wrongAnswersRes.success) setWrongAnswers(wrongAnswersRes.data || []);
      };
      fetchMistakes();
    }
  }, [activeTab, user]);

  // Forum rate limiting helpers
  const getPostsLast24Hours = () => {
    const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
    return myForumPosts.filter(p => p.created_at && p.created_at > twentyFourHoursAgo);
  };

  const getRemainingPostsToday = () => {
    const recentPosts = getPostsLast24Hours();
    return Math.max(0, FORUM_DAILY_LIMIT - recentPosts.length);
  };

  const getForumCooldownTime = () => {
    const recentPosts = getPostsLast24Hours();
    if (recentPosts.length < FORUM_DAILY_LIMIT) return null;
    // Find oldest post among the 5 most recent
    const sorted = [...recentPosts].sort((a, b) => a.created_at - b.created_at);
    const oldestOfFive = sorted[0];
    const cooldownEnds = oldestOfFive.created_at + (24 * 60 * 60 * 1000);
    return cooldownEnds;
  };

  const formatCooldownTime = (cooldownEnds) => {
    if (!cooldownEnds) return '';
    const diff = cooldownEnds - Date.now();
    if (diff <= 0) return '00:00';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleDeleteForumPost = async (postId) => {
    if (!confirm('هل أنت متأكد من حذف هذا البوست؟')) return;
    const res = await d1Api.deleteForumPost(postId);
    if (res.success) {
      setMyForumPosts(prev => prev.filter(p => p.id !== postId));
      setForumPosts(prev => prev.filter(p => p.id !== postId));
    } else {
      alert('فشل حذف البوست');
    }
  };

  const calculateStats = () => {
    // Total lectures from all enrolled courses
    const totalLectures = lectures.filter(l => 
      myCourses.some(c => c.id === l.course_id)
    ).length;
    // Completed lectures from assignments/completed API
    const watchedLectures = completedLectures.length || 0;
    const attendanceRate = userData?.attendanceRate || 0;
    const points = userData?.points || 0;

    return {
      totalLectures,
      watchedLectures,
      progress: totalLectures > 0 ? Math.round((watchedLectures / totalLectures) * 100) : 0,
      attendanceRate,
      points
    };
  };

  const stats = calculateStats();

  // Update forum cooldown timer every minute
  useEffect(() => {
    const cooldownEnds = getForumCooldownTime();
    setForumCooldownEnds(cooldownEnds);
    
    const interval = setInterval(() => {
      const newCooldownEnds = getForumCooldownTime();
      setForumCooldownEnds(newCooldownEnds);
    }, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [myForumPosts]);

  const isLectureLocked = (lecture, index, lecturesList) => {
    return false;
  };

  const getLectureQuizzes = (lec) => {
    let ids = [];
    try { if (lec.quiz_ids) ids = JSON.parse(lec.quiz_ids); } catch(e){}
    if (lec.required_quiz_id && String(lec.required_quiz_id).trim() !== 'null' && String(lec.required_quiz_id).trim() !== '') {
      ids.push(lec.required_quiz_id);
    }
    return [...new Set(ids)];
  };

  const handleSubmitAssignment = async (lectureId) => {
    const res = await d1Api.submitAssignment(user?.id, lectureId);
    if (res.success) {
      setCompletedLectures(prev => [...prev, lectureId]);
      alert('✅ تم تسليم الواجب بنجاح!');
    } else {
      alert(res.error || 'حدث خطأ أثناء تسليم الواجب');
    }
  };

  const getNextLecture = () => {
    const currentIndex = availableLectures.findIndex(l => l.id === selectedLecture?.id);
    if (currentIndex !== -1 && currentIndex < availableLectures.length - 1) {
      return availableLectures[currentIndex + 1];
    }
    return null;
  };

  const handleStartQuizWithConfirm = () => {
    if (selectedLecture?.quiz_id) {
      const quiz = quizzes.find(q => q.id === selectedLecture.quiz_id);
      if (quiz) {
        setCurrentQuiz(quiz);
        setShowQuizConfirm(true);
      } else {
        setCurrentQuiz({ id: selectedLecture.quiz_id, title: `اختبار: ${selectedLecture.title}` });
        setShowQuizConfirm(true);
      }
    } else if (selectedLecture?.homework_url) {
      window.open(selectedLecture.homework_url, '_blank');
      handleSubmitAssignment(selectedLecture.id);
    } else {
      alert('لا يوجد اختبار لهذه المحاضرة');
    }
  };

  const handleStartQuiz = async () => {
    setShowQuizConfirm(false);
    setQuizQuestionsLoading(true);
    setQuizQuestions([]);
    setQuizReadOnly(false);
    setQuizPreviousAnswers({});
    setQuizPreviousScore(0);
    setShowQuiz(true);
    try {
      // 1. Check if already attempted
      const attemptRes = await d1Api.getQuizAttempt(user.id, currentQuiz.id);
      if (attemptRes.success && attemptRes.data) {
        // Already attempted — open in read-only mode
        const attempt = attemptRes.data;
        let parsedAnswers = {};
        try { parsedAnswers = typeof attempt.answers === 'string' ? JSON.parse(attempt.answers) : (attempt.answers || {}); } catch { parsedAnswers = {}; }
        setQuizReadOnly(true);
        setQuizPreviousAnswers(parsedAnswers);
        setQuizPreviousScore(attempt.score || 0);
      }
      // 2. Fetch questions
      const res = await d1Api.getQuizQuestions(currentQuiz.id);
      if (res.success) {
        setQuizQuestions(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching quiz:', err);
    } finally {
      setQuizQuestionsLoading(false);
    }
  };

  const handleOpenAttemptReview = async (attempt) => {
    const quiz = { id: attempt.quiz_id, title: attempt.quiz_title || 'امتحان', duration_minutes: 0 };
    setCurrentQuiz(quiz);
    setQuizQuestionsLoading(true);
    setQuizQuestions([]);
    setQuizReadOnly(true);
    let parsedAnswers = {};
    try { parsedAnswers = typeof attempt.answers === 'string' ? JSON.parse(attempt.answers) : (attempt.answers || {}); } catch { parsedAnswers = {}; }
    setQuizPreviousAnswers(parsedAnswers);
    setQuizPreviousScore(attempt.score || 0);
    setShowQuiz(true);
    try {
      const res = await d1Api.getQuizQuestions(attempt.quiz_id);
      if (res.success) setQuizQuestions(res.data || []);
    } catch (err) {
      console.error('Error fetching quiz questions for review:', err);
    } finally {
      setQuizQuestionsLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (!centerCode.trim()) {
      setCodeError('الرجاء إدخال الكود');
      return;
    }

    setCodeValidating(true);
    setCodeError('');
    setCodeSuccess('');

    const result = await d1Api.validateCenterCode(centerCode.toUpperCase());
    
    if (!result.valid) {
      setCodeError(result.message);
      setCodeValidating(false);
      return;
    }

    const useResult = await d1Api.useCenterCode(centerCode.toUpperCase(), user.uid || user.id);
    
    if (useResult.success) {
      setCodeSuccess(useResult.data?.message || 'تم تفعيل الكورس بنجاح!');
      setCenterCode('');
      setTimeout(() => {
        setShowCodeModal(false);
        setCodeSuccess('');
        window.location.reload();
      }, 3000);
    } else {
      setCodeError(useResult.error || 'حدث خطأ أثناء تفعيل الكود');
    }
    setCodeValidating(false);
  };

  const getRankBadge = (points) => {
    if (points >= 150) return { text: 'أسطوري', class: 'bg-gradient-to-r from-yellow-500 to-amber-600' };
    if (points >= 100) return { text: 'متقدم', class: 'bg-gradient-to-r from-blue-500 to-cyan-600' };
    if (points >= 50) return { text: 'مجتهد', class: 'bg-gradient-to-r from-green-500 to-emerald-600' };
    return { text: 'مبتدئ', class: 'bg-gradient-to-r from-slate-500 to-zinc-600' };
  };

  const rank = getRankBadge(stats.points);

  const menuItems = [
    { id: 'dashboard',  label: 'الرئيسية',        Icon: LayoutDashboard },
    { id: 'mycourses',  label: 'كورساتي',          Icon: BookOpen,       count: myCourses.length },
    { id: 'lectures',   label: 'المحاضرات',        Icon: PlayCircle,     count: availableLectures.length },
    { id: 'exams',      label: 'امتحاناتي',        Icon: ClipboardList,  count: quizzes.filter(q => q.is_active !== 0).length },
    { id: 'mistakes',   label: 'صحح أخطاءك',      Icon: RefreshCw,      count: wrongAnswers.length },
    { id: 'messages',   label: 'الرسائل',          Icon: MessageSquare,  badge: unreadMessages > 0 ? unreadMessages : undefined },
    { id: 'forum',      label: 'المنتدى',          Icon: MessageSquare },
    { id: 'evaluation', label: 'تقييمي الشامل',    Icon: Star },
    { id: 'leaderboard', label: 'لوحة الشرف',       Icon: Award },
  ];

  if (!user || !userData) {
    return <Navigate to="/login" replace />;
  }

  if (userData.accountStatus === 'banned' || userData.accountStatus === 'suspended') {
    return <Navigate to="/suspended" replace />;
  }

  if (userData?.accountStatus === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-black">
        <div className="glass-panel p-8 max-w-md w-full text-center animate-scale-in">
          <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock size={40} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-4 text-white">
            حسابك قيد التفعيل ⏳
          </h2>
          <p className="mb-8 leading-relaxed text-gray-400">
            أهلاً بك يا بطل! تم استلام بياناتك بنجاح.
            <br />
            الإدارة تقوم بمراجعة حسابك حالياً، يرجى الانتظار لحين التفعيل.
          </p>
          <button
            onClick={logout}
            className="w-full bg-white hover:bg-gray-200 text-black p-4 rounded-xl font-bold flex items-center justify-center gap-3 transition shadow-lg"
          >
            تسجيل الخروج
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardLayout
        menuItems={menuItems}
        activeTab={activeTab}
        setActiveTab={(tab) => { 
          setActiveTab(tab); 
          setSelectedLecture(null);
          setForceResetKey(prev => prev + 1);
        }}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        signOut={logout}
        profile={userData}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        bottomSlot={
          <button
            onClick={() => setShowCodeModal(true)}
            className={`w-full p-3 rounded-xl font-bold flex items-center justify-center gap-2 transition shadow-lg ${isDarkMode ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}`}
          >
            <Gift size={18} />
            لدي كود سنتر
          </button>
        }
      >
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="glass-panel p-8">
              <h1 className="text-3xl font-black mb-2">
                مرحباً {([userData?.firstName, userData?.lastName].filter(Boolean).join(' ') || userData?.name || 'طالب')} 👋
              </h1>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-slate-400 text-sm">
                  الصف: {userData?.grade === '1sec' ? 'أولى ثانوي' : userData?.grade === '2sec' ? 'تانية ثانوي' : userData?.grade === '3sec' ? 'تالتة ثانوي' : 'غير محدد'}
                </span>
              </div>
              <p className="text-slate-400 mb-4">
                {userData?.bio || 'مرحباً بك في منصة سبيل التفوق'}
              </p>

              {announcements.length > 0 && (
                <div className="space-y-3 mt-4">
                  {announcements.map((announcement) => (
                    <div
                      key={announcement.id}
                      className="glass-panel p-5 border border-white/20 rounded-2xl flex items-start gap-4"
                    >
                      <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Bell size={24} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-white mb-1">{announcement.title}</h3>
                        <p className="text-slate-300 leading-relaxed">{announcement.content}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          {announcement.created_at ? new Date(Number(announcement.created_at) || announcement.created_at).toLocaleDateString('ar-EG') : '—'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="glass-panel p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <BookOpen size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{activeCourses.length}</div>
                    <div className="text-xs text-slate-400">كورسات مفعلة</div>
                  </div>
                </div>
              </div>

              <div className="glass-panel p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Video size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{availableLectures.length}</div>
                    <div className="text-xs text-slate-400">محاضرة متاحة</div>
                  </div>
                </div>
              </div>

              <div className="glass-panel p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <TrendingUp size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats.progress}%</div>
                    <div className="text-xs text-slate-400">تقدم المشاهدة</div>
                  </div>
                </div>
              </div>

              <div className="glass-panel p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Award size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats.points}</div>
                    <div className="text-xs text-slate-400">نقاط التميز</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-panel p-6">
              <h3 className="font-bold mb-4">📊 تقدمك العام</h3>
              <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-500"
                  style={{ width: `${stats.progress}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-2 text-sm text-slate-400">
                <span>شاهدت {stats.watchedLectures} من {stats.totalLectures} محاضرة</span>
                <span>{stats.progress}%</span>
              </div>
            </div>

            {myCourses.length > 0 && (
              <div className="glass-panel p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">📚 أحدث كورساتك</h3>
                  <button
                    onClick={() => setActiveTab('mycourses')}
                    className="text-brand-red hover:text-brand-gold text-sm font-bold"
                  >
                    عرض الكل ←
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {activeCourses.slice(0, 2).map((course) => (
                    <div
                      key={course.id}
                      className="bg-white/5 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:bg-white/10 transition"
                      onClick={() => {
                        setSelectedCourse(course);
                        setActiveTab('lectures');
                      }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                        <BookOpen size={20} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold">{course.title}</h4>
                        <p className="text-xs text-slate-400">{course.duration_days || course.duration} يوم</p>
                      </div>
                    </div>
                  ))}
                  {expiredCourses.length > 0 && activeCourses.length < 2 && expiredCourses.slice(0, 2 - activeCourses.length).map((course) => (
                    <div
                      key={course.id}
                      className="bg-white/5 rounded-xl p-4 flex items-center gap-3 opacity-50 cursor-not-allowed relative"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center">
                        <Lock size={20} className="text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-400">{course.title}</h4>
                        <p className="text-xs text-red-400 font-bold">⛔ منتهي الصلاحية</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {wrongAnswers.length > 0 && (
              <div className="glass-panel p-6 border border-white/20 rounded-2xl">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                      <RefreshCw size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-1">📝 صحح أخطاءك!</h3>
                      <p className="text-slate-300">
                        لديك {wrongAnswers.length} سؤال/أسئلة خاطئة. راجعها لتحسين مستواك.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab('mistakes')}
                    className="px-6 py-3 bg-white hover:bg-gray-200 text-black font-bold rounded-xl transition flex items-center gap-2"
                  >
                    <RefreshCw size={18} />
                    راجع الأخطاء
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'mycourses' && (
          <div className="space-y-6">
            {/* Section 1: Enrolled Courses */}
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 text-green-500 flex items-center justify-center">
                <BookOpen size={20} />
              </div>
              <h2 className="text-2xl font-bold">📚 كورساتي المفعلة</h2>
            </div>
            
            {myCourses.length === 0 ? (
              <div className="glass-panel p-12 text-center">
                <BookOpen size={48} className="mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-bold mb-2">لا توجد كورسات مفعلة</h3>
                <p className="text-slate-400 mb-6">اشترك في كورس جديد وابدأ رحلة التعلم</p>
              </div>
            ) : (
              <>
                {/* Active Courses */}
                {activeCourses.length > 0 && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activeCourses.map((course) => (
                      <div
                        key={course.id}
                        className="glass-panel overflow-hidden group cursor-pointer hover:border-green-500/30 transition"
                        onClick={() => {
                          setSelectedCourse(course);
                          setActiveTab('lectures');
                        }}
                      >
                        <div className="h-40 bg-gradient-to-br from-slate-800 to-slate-900 relative">
                          {course.image_url && <img src={course.image_url} alt={course.title} className="w-full h-full object-cover opacity-70" />}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                          <span className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1 z-20">
                            <CheckCircle size={14} />
                            مشترك ✔️
                          </span>
                          <div className="absolute bottom-4 right-4">
                            <h3 className="font-bold text-lg">{course.title}</h3>
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-sm text-slate-400 line-clamp-2 mb-3">{course.description}</p>
                          <div className="flex justify-between items-center">
                            <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded">
                              مفعل ✓
                            </span>
                            <button className="text-brand-red hover:text-brand-gold text-sm font-bold">
                              عرض المحاضرات ←
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Expired Courses — Greyed out with badge */}
                {expiredCourses.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-bold text-slate-400 mb-4 flex items-center gap-2">
                      <Clock size={20} className="text-red-400" />
                      كورسات منتهية الصلاحية ({expiredCourses.length})
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {expiredCourses.map((course) => (
                        <div
                          key={course.id}
                          className="glass-panel overflow-hidden opacity-50 cursor-not-allowed relative"
                        >
                          {/* Expired overlay */}
                          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] z-10 flex items-center justify-center">
                            <div className="bg-red-600/90 text-white px-5 py-2.5 rounded-xl font-black text-sm flex items-center gap-2 shadow-lg shadow-red-900/40">
                              <Lock size={16} />
                              منتهي الصلاحية
                            </div>
                          </div>
                          <div className="h-40 bg-gradient-to-br from-slate-800 to-slate-900 relative">
                            {course.image_url && <img src={course.image_url} alt={course.title} className="w-full h-full object-cover opacity-40 grayscale" />}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                            <div className="absolute bottom-4 right-4">
                              <h3 className="font-bold text-lg text-slate-400">{course.title}</h3>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-slate-500 line-clamp-2 mb-3">{course.description}</p>
                            <div className="flex justify-between items-center">
                              <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded font-bold">
                                ⛔ منتهي الصلاحية
                              </span>
                              <span className="text-slate-500 text-xs">
                                {course.fixed_expiration_date
                                  ? `انتهى ${new Date(Number(course.fixed_expiration_date)).toLocaleDateString('ar-EG')}`
                                  : course.expires_at
                                    ? `انتهى ${new Date(Number(course.expires_at)).toLocaleDateString('ar-EG')}`
                                    : ''
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Section 2: Available Courses (Store merged here) */}
            {(() => {
              const enrolledIds = new Set(myCourses.map(c => c.id));
              const unenrolledCourses = suggestedCourses.filter(c => c.status !== 'hidden' && !enrolledIds.has(c.id));
              if (unenrolledCourses.length === 0) return null;
              return (
                <div className="mt-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-500 flex items-center justify-center">
                      <ShoppingCart size={20} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">🛒 كورسات متاحة للاشتراك</h2>
                      <p className="text-slate-400 text-sm">اشترك الآن وابدأ رحلة التفوق</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {unenrolledCourses.map((course) => {
                      const isLocked = course.status === 'locked';
                      return (
                        <div
                          key={course.id}
                          className={`glass-panel overflow-hidden group transition ${isLocked ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer hover:scale-[1.02] hover:border-brand-gold/40'}`}
                          onClick={() => {
                            if (isLocked) return;
                            setSelectedCourse(course);
                            setShowPurchaseModal(true);
                          }}
                        >
                          <div className="h-40 bg-gradient-to-br from-slate-800 to-slate-900 relative">
                            {course.image_url && <img src={course.image_url} alt={course.title} className="w-full h-full object-cover opacity-70" />}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                            {isLocked && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <div className="bg-black/70 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/20 text-white font-bold text-sm">
                                  🔒 الحجز مغلق
                                </div>
                              </div>
                            )}
                            <span className="absolute top-4 right-4 bg-brand-red/90 text-white px-2 py-1 rounded text-xs font-bold">
                              {course.grade === '1sec' ? 'أولى ثانوي' : course.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}
                            </span>
                            <div className="absolute bottom-4 left-4 bg-brand-gold text-brand-dark px-3 py-1 rounded-xl font-black">
                              {course.price === 0 ? 'مجاني' : `${course.price} ج.م`}
                            </div>
                          </div>
                          <div className="p-4">
                            <h3 className="font-bold text-lg mb-2">{course.title}</h3>
                            <p className="text-sm text-slate-400 line-clamp-2 mb-3">{course.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs flex items-center gap-1 text-slate-400">
                                <Clock size={12} />
                                {course.duration_days || course.duration} يوم
                              </span>
                              {isLocked ? (
                                <span className="text-slate-500 text-sm font-bold cursor-not-allowed">
                                  🔒 الحجز مغلق
                                </span>
                              ) : (
                                <button className="bg-gradient-to-r from-brand-red to-brand-gold text-white px-4 py-2 rounded-xl text-sm font-bold hover:opacity-90 transition">
                                  التفاصيل / اشترك الآن 🛒
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}


        {activeTab === 'lectures' && (
          <div className="space-y-6">
            {selectedLecture ? (
              <div className="space-y-8">
                <button
                  onClick={() => setSelectedLecture(null)}
                  className="flex items-center gap-2 text-slate-400 hover:text-white font-bold transition-colors group"
                >
                  <ChevronRight size={20} className="group-hover:-translate-x-1 transition-transform" />
                  <span>العودة للمحاضرات</span>
                </button>

                {/* Video Parts Tabs - RESTORED ISLANDS DESIGN */}
                {videoParts.length > 1 && (
                  <div className="rounded-3xl border border-white/10 bg-zinc-900/50 backdrop-blur-md p-4 mb-2">
                    <div className="flex gap-3 overflow-x-auto custom-scrollbar pb-1">
                      {videoParts.map((video, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setCurrentVideoPart(idx);
                            const videoSection = document.querySelector('.video-section');
                            if (videoSection) {
                              videoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                          className={`px-5 py-2.5 rounded-full font-bold whitespace-nowrap transition-all duration-200 flex items-center gap-2 ${
                            currentVideoPart === idx
                              ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-600/30'
                              : 'bg-black/40 text-slate-300 hover:bg-white/10 hover:text-white border border-white/10'
                          }`}
                        >
                          <Film size={16} className={currentVideoPart === idx ? 'text-white' : 'text-slate-400'} />
                          {(typeof video === 'string' ? `الجزء ${idx + 1}` : video.title) || `الجزء ${idx + 1}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="relative group video-section">
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/30 to-blue-600/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition duration-500"></div>
<div className="relative rounded-2xl bg-black/60 backdrop-blur-md border border-white/10 shadow-2xl z-40">                    {videoParts.length > 0 ? (
                      <LecturePlayer
                        key={`${selectedLecture.id}-${currentVideoPart}-${forceResetKey}`}
                        lecture={selectedLecture}
                        userEnrolledCourses={myCourses ?? []}
                        isLoading={dataLoading}
                        studentPhone={userData?.phone || user?.phone || '01xxxxxxxxx'}
                        isLocked={false}
                        currentVideoPart={currentVideoPart}
                        setCurrentVideoPart={setCurrentVideoPart}
                      />
                    ) : (
                      <div className="aspect-video bg-black rounded-[24px] flex items-center justify-center border border-white/10">
                        <div className="text-center text-slate-400">
                          <VideoOff size={48} className="mx-auto mb-3 opacity-50" />
                          <p>لا يوجد فيديو لهذه المحاضرة</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-white">{selectedLecture.title}</h2>
                  {selectedLecture.description && (
                    <p className="text-slate-400 leading-relaxed">{selectedLecture.description}</p>
                  )}
                </div>

                <div id="quizzes-section" className="glass-panel rounded-3xl bg-black/40 backdrop-blur-xl border border-white/10 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-6 pb-2 border-b border-white/10">
                      <BookMarked size={20} className="text-brand-gold" />
                      <h3 className="text-lg font-bold text-white">محتويات المحاضرة</h3>
                    </div>
                    
                    <div className="space-y-3">
                      {pdfs.map((pdf, index) => (
                        <div key={index} className="flex items-center justify-between p-5 bg-white/5 hover:bg-white/10 border border-white/5 rounded-2xl transition-all duration-200 group">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                              <FileText size={20} className="text-green-400" />
                            </div>
                            <div>
                              <p className="font-bold text-white">{pdf.title || 'ملف تعليمي'}</p>
                              <p className="text-xs text-slate-400">PDF - مستند تعليمي</p>
                            </div>
                          </div>
                          <a
                            href={pdf.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-xl font-bold text-white transition-all duration-200 flex items-center gap-2 shadow-lg shadow-green-600/20"
                          >
                            <Download size={16} />
                            فتح الآن
                          </a>
                        </div>
                      ))}

                      {selectedLecture.homework_url && (
                        <div className="flex items-center justify-between p-5 bg-white/5 hover:bg-white/10 border border-white/5 rounded-2xl transition-all duration-200 group">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                              <ClipboardList size={20} className="text-purple-400" />
                            </div>
                            <div>
                              <p className="font-bold text-white">واجب المحاضرة</p>
                              <p className="text-xs text-slate-400">حل الواجب لتثبيت المعلومات</p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              window.open(selectedLecture.homework_url, '_blank');
                              handleSubmitAssignment(selectedLecture.id);
                            }}
                            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-xl font-bold text-white transition-all duration-200 flex items-center gap-2 shadow-lg shadow-purple-600/20"
                          >
                            <ClipboardList size={16} />
                            حل الواجب
                          </button>
                        </div>
                      )}

                      {/* عرض الشيتات المرفقة تحت فيديو المحاضرة مباشرة */}
                      {lectureQuizzes.map((quiz, idx) => (
                        <div key={quiz.id || idx} className="flex items-center justify-between p-5 bg-white/5 hover:bg-white/10 border border-white/5 rounded-2xl transition-all duration-200 group">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                              <FileQuestion size={20} className="text-amber-400" />
                            </div>
                            <div>
                              <p className="font-bold text-white">{quiz.title || 'اختبار المحاضرة'}</p>
                              <p className="text-xs text-slate-400">حل الشيت الآن لتقييم فهمك</p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setCurrentQuiz(quiz);
                              setShowQuizConfirm(true);
                            }}
                            className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 rounded-xl font-bold text-white transition-all duration-200 flex items-center gap-2 shadow-lg shadow-amber-600/20"
                          >
                            <ClipboardList size={16} />
                            {myAttempts.some(a => a.quiz_id === quiz.id) ? 'عرض النتيجة' : 'حل الشيت'}
                          </button>
                        </div>
                      ))}

                      {selectedLecture.external_link && !selectedLecture.file_url && !selectedLecture.quiz_id && !selectedLecture.homework_url && pdfs.length === 0 && (
                        <div className="flex items-center justify-between p-5 bg-white/5 hover:bg-white/10 border border-white/5 rounded-2xl transition-all duration-200 group">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gray-500/20 flex items-center justify-center">
                              <ExternalLink size={20} className="text-gray-400" />
                            </div>
                            <div>
                              <p className="font-bold text-white">رابط إضافي (يفتح في نافذة جديدة)</p>
                              <p className="text-xs text-slate-400">محتوى إضافي للمحاضرة</p>
                            </div>
                          </div>
                          <a
                            href={selectedLecture.external_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-5 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 rounded-xl font-bold text-white transition-all duration-200 flex items-center gap-2 shadow-lg shadow-gray-600/20"
                          >
                            <ExternalLink size={16} />
                            عرض الرابط
                          </a>
                        </div>
                      )}

                      {pdfs.length === 0 && 
                       lectureQuizzes.length === 0 && 
                       !selectedLecture.homework_url && 
                       !selectedLecture.external_link && (
                        <div className="text-center py-12">
                          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                            <FolderOpen size={40} className="text-slate-500" />
                          </div>
                          <p className="text-slate-400 text-lg">لا توجد ملفات أو اختبارات مرفقة بهذه المحاضرة</p>
                          <p className="text-slate-500 text-sm mt-2">سيتم إضافة المحتوى قريباً إن شاء الله</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {(() => {
                  if (hasMultipleParts && !isLastPart) {
                    return (
                      <div className="flex justify-end pt-4">
                        <button
                          onClick={() => {
                            setCurrentVideoPart(prev => prev + 1);
                            const videoSection = document.querySelector('.video-section');
                            if (videoSection) {
                              videoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                          className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 rounded-xl font-bold text-white transition-all duration-300 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40"
                        >
                          <span className="relative z-10">الفيديو التالي</span>
                          <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity blur-xl"></div>
                        </button>
                      </div>
                    );
                  }
                  
                    const nextLecture = getNextLecture();
                    if (nextLecture) {
                      // Strict Locking for next lecture
                      const requiredQuizIdNext = nextLecture.required_quiz_id;
                      const hasRequiredQuizNext = requiredQuizIdNext && String(requiredQuizIdNext).trim() !== '' && String(requiredQuizIdNext).trim() !== 'null';
                      const isNextLockedByQuiz = nextLecture.is_quiz_required === 1 && hasRequiredQuizNext && !myAttempts.some(a => a.quiz_id === requiredQuizIdNext);
                      
                      // Enrollment/Preview access check for next lecture
                      const isNextEnrolled = enrolledCourseIds.includes(nextLecture.course_id);
                      const isNextPreview = nextLecture.is_preview === 1 || nextLecture.is_preview === true;
                      const isNextFree = nextLecture.is_free || nextLecture.isFree;
                      const isNextAccessible = isNextEnrolled || isNextPreview || isNextFree;
                      const isNextLocked = isNextLockedByQuiz || !isNextAccessible;

                      return (
                        <div className="flex justify-end pt-4">
                          <button
                            onClick={() => {
                              if (!isNextAccessible) {
                                // Redirect to mycourses tab (payment) with toast
                                alert('اشترك في الكورس لتتمكن من مشاهدة باقي المحاضرات');
                                setSelectedLecture(null);
                                setSelectedCourse(null);
                                setActiveTab('mycourses');
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                return;
                              }
                              if (isNextLockedByQuiz) {
                                // Scroll to quizzes section so user can see the quiz they need to solve
                                document.getElementById('quizzes-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                return;
                              }
                              setCurrentVideoPart(0);
                              setSelectedLecture(nextLecture);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className={`group relative inline-flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 shadow-lg ${
                              !isNextAccessible
                                ? 'bg-gradient-to-r from-red-700 to-red-500 hover:from-red-800 hover:to-red-600 cursor-pointer border border-red-500/40 shadow-red-600/20 hover:shadow-red-600/40'
                                : isNextLockedByQuiz
                                  ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 cursor-pointer border border-orange-500/40 shadow-orange-600/20 hover:shadow-orange-600/40'
                                  : 'bg-gradient-to-r from-brand-red to-brand-gold hover:from-brand-red/90 hover:to-brand-gold/90 shadow-brand-red/20 hover:shadow-brand-red/40'
                            }`}
                          >
                            <span className="relative z-10">
                              {!isNextAccessible
                                ? 'اشترك في الكورس لمشاهدة المحاضرة التالية 🔒'
                                : isNextLockedByQuiz
                                  ? 'اذهب لحل الامتحان لفتح المحاضرة التالية 🔒'
                                  : 'المحاضرة التالية'
                              }
                            </span>
                            {!isNextAccessible ? (
                              <ShoppingCart size={20} className="relative z-10 text-red-200" />
                            ) : isNextLockedByQuiz ? (
                              <ClipboardList size={20} className="relative z-10 text-orange-200" />
                            ) : (
                              <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform relative z-10" />
                            )}
                            {isNextAccessible && !isNextLockedByQuiz && <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-brand-red to-brand-gold opacity-0 group-hover:opacity-100 transition-opacity blur-xl"></div>}
                          </button>
                        </div>
                      );
                  }
                  
                  return (
                    <div className="text-center py-8 mt-4">
                      <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-600/20 to-green-600/20 rounded-full border border-emerald-500/30">
                        <Sparkles size={20} className="text-emerald-400" />
                        <span className="text-emerald-400 font-bold">🎉 مبروك! لقد أنهيت جميع محاضرات هذا الكورس!</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold">🎥 المحاضرات</h2>
                
                {availableLectures.length === 0 ? (
                  <div className="glass-panel p-12 text-center">
                    <VideoOff size={48} className="mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-bold mb-2">لا توجد محاضرات متاحة</h3>
                    <p className="text-slate-400">اشترك في كورس أولاً لمشاهدة المحاضرات</p>
                  </div>
                ) : (() => {
                  // Group lectures by course_id
                  const courseGroups = [];
                  const courseMap = new Map();
                  availableLectures.forEach(lec => {
                    const cid = lec.course_id || 'unknown';
                    if (!courseMap.has(cid)) {
                      const courseName = lec.course_title || suggestedCourses.find(c => c.id === cid)?.title || myCourses.find(c => c.id === cid)?.title || 'كورس غير معروف';
                      const group = { courseId: cid, courseName, lectures: [] };
                      courseMap.set(cid, group);
                      courseGroups.push(group);
                    }
                    courseMap.get(cid).lectures.push(lec);
                  });

                  return courseGroups.map(group => (
                    <div key={group.courseId} className="mb-10">
                      {/* Course Group Header */}
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-brand-red/20 text-brand-red flex items-center justify-center flex-shrink-0">
                          <BookOpen size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-bold text-white truncate">{group.courseName}</h3>
                          <p className="text-xs text-slate-400">{group.lectures.length} محاضرة</p>
                        </div>
                        {enrolledCourseIds.includes(group.courseId) ? (
                          <span className="text-xs bg-green-500/20 text-green-300 px-3 py-1 rounded-full font-bold flex items-center gap-1 flex-shrink-0">
                            <CheckCircle size={12} />
                            مشترك
                          </span>
                        ) : (
                          <button
                            onClick={() => {
                              const course = suggestedCourses.find(c => c.id === group.courseId);
                              if (course) {
                                setSelectedCourse(course);
                                setShowPurchaseModal(true);
                              } else {
                                setActiveTab('mycourses');
                              }
                            }}
                            className="text-xs bg-brand-red/20 text-brand-red px-3 py-1 rounded-full font-bold hover:bg-brand-red hover:text-white transition flex items-center gap-1 flex-shrink-0"
                          >
                            <ShoppingCart size={12} />
                            اشترك الآن
                          </button>
                        )}
                      </div>

                      {/* Lectures Grid for this course */}
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {group.lectures.map((lecture, idx) => {
                          const locked = isLectureLocked(lecture, idx, group.lectures);
                          const requiredQuizId = lecture.required_quiz_id;
                          const hasRequiredQuiz = requiredQuizId && String(requiredQuizId).trim() !== '' && String(requiredQuizId).trim() !== 'null';
                          const isLockedByQuiz = lecture.is_quiz_required === 1 && hasRequiredQuiz && !myAttempts.some(a => a.quiz_id === requiredQuizId);
                          const isEnrolledInCourse = enrolledCourseIds.includes(lecture.course_id);
                          const isPreviewLecture = !isEnrolledInCourse && (lecture.is_preview === 1 || lecture.is_preview === true);
                          const isNotEnrolledAndNotPreview = !isEnrolledInCourse && !lecture.is_free && !lecture.isFree && !isPreviewLecture;
                          const isAnyLocked = locked || isLockedByQuiz || isNotEnrolledAndNotPreview;
                          return (
                            <div key={lecture.id} className="relative">
                              {locked && !isLockedByQuiz && !isNotEnrolledAndNotPreview && (
                                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                                  <div className="text-center">
                                    <Lock size={32} className="text-red-500 mx-auto mb-2" />
                                    <p className="text-sm font-bold">أكمل المحاضرة السابقة أولاً</p>
                                  </div>
                                </div>
                              )}
                              {isNotEnrolledAndNotPreview && (
                                <div 
                                  className="absolute inset-0 bg-black/60 backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center cursor-pointer"
                                  onClick={() => {
                                    alert('اشترك في الكورس لتتمكن من مشاهدة باقي المحاضرات');
                                    setSelectedLecture(null);
                                    setSelectedCourse(null);
                                    setActiveTab('mycourses');
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }}
                                >
                                  <div className="text-center">
                                    <Lock size={32} className="text-red-500 mx-auto mb-2" />
                                    <p className="text-sm font-bold">اشترك في الكورس لمشاهدة المحاضرة</p>
                                    <p className="text-xs text-slate-400 mt-1">اضغط هنا للاشتراك 🛒</p>
                                  </div>
                                </div>
                              )}
                              <div
                                onClick={() => {
                                  if (isNotEnrolledAndNotPreview) {
                                    alert('اشترك في الكورس لتتمكن من مشاهدة باقي المحاضرات');
                                    setSelectedLecture(null);
                                    setSelectedCourse(null);
                                    setActiveTab('mycourses');
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                    return;
                                  }
                                  if (!isAnyLocked) setSelectedLecture(lecture);
                                }}
                                className={`glass-panel overflow-hidden cursor-pointer group hover:border-brand-red/50 transition ${isAnyLocked ? 'opacity-75 border-red-500/30' : ''}`}
                              >
                                <div className="h-40 bg-gradient-to-br from-slate-800 to-slate-900 relative flex items-center justify-center">
                                  {lecture.thumbnail_url && <img src={lecture.thumbnail_url} alt={lecture.title} className="absolute inset-0 w-full h-full object-cover opacity-50" />}
                                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition z-10"></div>
                                  {isNotEnrolledAndNotPreview
                                    ? <Lock size={48} className="text-red-400 opacity-70 group-hover:opacity-100 group-hover:scale-110 transition" />
                                    : isLockedByQuiz
                                      ? <Lock size={48} className="text-red-400 opacity-70 group-hover:opacity-100 group-hover:scale-110 transition" />
                                      : locked
                                        ? <Lock size={48} className="text-white opacity-70 group-hover:opacity-100 group-hover:scale-110 transition" />
                                        : <PlayCircle size={48} className="text-white opacity-70 group-hover:opacity-100 group-hover:scale-110 transition" />
                                  }
                                  {lecture.is_free && (
                                    <span className="absolute top-4 right-4 bg-green-600 text-white px-2 py-1 rounded text-xs font-bold">
                                      مجاني
                                    </span>
                                  )}
                                  {isPreviewLecture && (
                                    <span className="absolute top-4 right-4 bg-cyan-600 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1 z-20">
                                      <Eye size={12} />
                                      معاينة مجانية
                                    </span>
                                  )}
                                </div>
                                <div className="p-4">
                                  <h3 className="font-bold mb-2">{lecture.title}</h3>
                                  <p className="text-xs text-slate-400 line-clamp-2">{lecture.description}</p>
                                  {isLockedByQuiz && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const res = await d1Api.getQuizQuestions(requiredQuizId);
                                          if (res.success && res.data) {
                                            const quizInfo = quizzes.find(q => q.id === requiredQuizId);
                                            setCurrentQuiz({ 
                                              id: requiredQuizId, 
                                              title: quizInfo?.title || 'الشيت الإجباري', 
                                              isCombo: false 
                                            });
                                            setQuizQuestions(res.data);
                                            setQuizReadOnly(false);
                                            setShowQuiz(true);
                                          } else {
                                            alert('لم يتم العثور على أسئلة الامتحان');
                                          }
                                        } catch(err) {
                                          alert('حدث خطأ أثناء تحميل الامتحان');
                                        }
                                      }}
                                      className="mt-3 w-full py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition shadow-lg"
                                    >
                                      <ClipboardList size={16} />
                                      ابدأ الامتحان لفتح المحاضرة
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
              </>
            )}
          </div>
        )}

        {activeTab === 'exams' && (
          <div className="space-y-6 animate-fade-in" dir="rtl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 text-white flex items-center justify-center shadow-lg">
                <ClipboardList size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">امتحاناتي 📝</h2>
                <p className="text-slate-400 text-sm">كل الامتحانات المتاحة في كورساتك — حلّها في أي وقت</p>
              </div>
            </div>

            {(() => {
              const courseNameById = {};
              [...(myCourses || []), ...(suggestedCourses || [])].forEach(c => { courseNameById[c.id] = c.title; });
              const visibleQuizzes = (quizzes || []).filter(q => q.is_active !== 0);
              if (visibleQuizzes.length === 0) {
                return (
                  <div className="glass-panel p-16 text-center">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                      <FileQuestion size={40} className="text-slate-500" />
                    </div>
                    <p className="text-slate-400 text-lg">لا توجد امتحانات متاحة حالياً</p>
                    <p className="text-slate-500 text-sm mt-2">سيتم إضافة الامتحانات قريباً إن شاء الله</p>
                  </div>
                );
              }
              return (
                <div className="grid md:grid-cols-2 gap-4">
                  {visibleQuizzes.map((quiz) => {
                    const attempt = myAttempts.find(a => a.quiz_id === quiz.id);
                    return (
                      <div key={quiz.id} className="glass-panel p-6 flex items-center justify-between gap-4 hover:border-purple-500/40 transition">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center shrink-0">
                            <FileQuestion size={24} className="text-purple-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-white truncate">{quiz.title || 'امتحان بدون عنوان'}</p>
                            <p className="text-xs text-slate-400 truncate">
                              {courseNameById[quiz.course_id] || 'كورس'}
                              {quiz.duration_minutes ? ` • ${quiz.duration_minutes} دقيقة` : ''}
                            </p>
                            {attempt && (
                              <p className="text-xs font-bold text-green-400 mt-1">
                                درجتك: {attempt.score} / {attempt.total}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setCurrentQuiz(quiz);
                            setShowQuizConfirm(true);
                          }}
                          className="shrink-0 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-xl font-bold text-white transition-all duration-200 flex items-center gap-2 shadow-lg shadow-purple-600/20"
                        >
                          <ClipboardList size={16} />
                          {attempt ? 'عرض النتيجة' : 'ابدأ الامتحان'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'mistakes' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 text-amber-500 flex items-center justify-center">
                <RefreshCw size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">صحح أخطاءك</h2>
                <p className="text-slate-400 text-sm">راجع الأسئلة التي أخطأت فيها لتثبيت المعلومات</p>
              </div>
            </div>

            {wrongAnswers.length === 0 ? (
              <div className="glass-panel p-12 text-center text-emerald-400">
                <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={40} className="text-emerald-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">عمل رائع! مفيش أخطاء مسجلة ليك</h3>
                <p className="opacity-80">استمر في التفوق</p>
              </div>
            ) : (
              <>
                {/* Horizontal Carousel */}
                <div className="relative">
                  <div 
                    className="flex gap-6 overflow-x-auto pb-4 custom-scrollbar"
                    style={{ scrollSnapType: 'x mandatory' }}
                  >
                    {wrongAnswers.map(wa => (
                      <div 
                        key={wa.id} 
                        className="glass-panel p-6 border-t-2 border-brand-red flex-shrink-0"
                        style={{ minWidth: '350px', maxWidth: '420px', scrollSnapAlign: 'start' }}
                      >
                        <p className="text-lg font-bold text-white mb-6 leading-relaxed bg-white/5 p-4 rounded-xl">
                          {wa.question_text}
                        </p>
                        
                        <div className="space-y-3 mb-4">
                          {['a', 'b', 'c', 'd'].map(optKey => {
                            const isCorrect = wa.correct_answer === optKey;
                            const isUserAnswer = wa.user_answer === optKey;
                            
                            if (!wa[`option_${optKey}`]) return null;

                            let btnClass = "w-full p-3 rounded-lg border text-right font-bold transition flex justify-between items-center ";
                            if (isCorrect) btnClass += "bg-emerald-500/20 border-emerald-500/50 text-emerald-300";
                            else if (isUserAnswer) btnClass += "bg-red-500/20 border-red-500/50 text-red-300 opacity-60 line-through";
                            else btnClass += "bg-white/5 border-white/10 text-slate-400 opacity-50";

                            return (
                              <div key={optKey} className={btnClass}>
                                <span>{wa[`option_${optKey}`]}</span>
                                {isCorrect && <span className="text-xl">✓ الصحيح</span>}
                                {isUserAnswer && !isCorrect && <span className="text-xs">إجابتك ✗</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Scroll hint */}
                  {wrongAnswers.length > 1 && (
                    <div className="text-center mt-2">
                      <span className="text-xs text-slate-500">← اسحب للتنقل بين الأسئلة →</span>
                    </div>
                  )}
                </div>

                {/* Count Badge */}
                <div className="text-center">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-sm font-bold">
                    <RefreshCw size={16} />
                    عندك {wrongAnswers.length} سؤال محتاج مراجعة
                  </span>
                </div>

                {/* Primary CTA: Create Custom Quiz */}
                <div className="text-center">
                  <button
                    onClick={() => {
                      // Convert wrongAnswers to quiz question format
                      const customQuestions = wrongAnswers.map(wa => ({
                        id: wa.id,
                        question_text: wa.question_text,
                        option_a: wa.option_a,
                        option_b: wa.option_b,
                        option_c: wa.option_c,
                        option_d: wa.option_d,
                        correct_answer: wa.correct_answer,
                      }));
                      setCurrentQuiz({ id: 'custom-mistakes', title: 'امتحان أخطائي 📝', isCombo: false });
                      setQuizQuestions(customQuestions);
                      setQuizReadOnly(false);
                      setQuizPreviousAnswers(null);
                      setQuizPreviousScore(null);
                      setShowQuiz(true);
                    }}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-brand-red to-brand-gold text-white rounded-2xl font-bold text-lg hover:opacity-90 transition-all duration-300 shadow-lg shadow-brand-red/20 hover:shadow-brand-red/40 hover:scale-[1.02]"
                  >
                    <ClipboardList size={24} />
                    إنشاء امتحان خاص بأخطائي 📝
                  </button>
                  <p className="text-slate-500 text-xs mt-3">سيتم إنشاء امتحان يحتوي فقط على الأسئلة التي أخطأت فيها</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Fix 2: Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 text-blue-500 flex items-center justify-center">
                <Bell size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">الرسائل من الإدارة</h2>
                <p className="text-slate-400 text-sm">رسائل موجهة لك من إدارة المنصة</p>
              </div>
            </div>

            {messages.length === 0 ? (
              <div className="glass-panel p-12 text-center text-slate-400">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                  <MessageSquare size={40} className="text-slate-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">لا توجد رسائل</h3>
                <p>لم تصلك أي رسائل من الإدارة بعد</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map(msg => (
                  <div key={msg.id} className="glass-panel p-6 border-l-4 border-blue-500">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <span className="text-blue-400 font-bold text-sm">
                            {(msg.adminFirstName || 'إ')[0]}
                          </span>
                        </div>
                        <span className="font-bold text-white">
                          {msg.adminFirstName || 'إدارة'} {msg.adminLastName || 'المنصة'}
                        </span>
                      </div>
                      <span className="text-xs text-slate-400">
                        {new Date(msg.created_at || Date.now()).toLocaleString('ar-EG')}
                      </span>
                    </div>
                    <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'forum' && (
          <div className="space-y-6 animate-fade-in" dir="rtl">
            {/* Fix 3: Forum Block Status */}
            {isBlockedFromForum && (
              <div className="glass-panel p-6 bg-red-500/10 border-2 border-red-500/30">
                <div className="flex items-center gap-3">
                  <Ban size={24} className="text-red-400" />
                  <div>
                    <h3 className="text-lg font-bold text-red-400">أنت محظور من المنتدى</h3>
                    <p className="text-slate-300 text-sm">تم منعك من النشر في المنتدى بسبب مخالفات سابقة. تواصل مع الإدارة لمراجعة الحظر.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="glass-panel p-6">
              <h2 className="text-2xl font-bold mb-2">💬 منتدى الطلاب</h2>
              <p className="text-slate-400 text-sm">اكتب سؤالك أو تعليقك. سيتم نشره بعد مراجعة الإدارة.</p>

              {/* Daily limit indicator */}
              <div className="mt-3 flex items-center justify-between">
                <span className={`text-sm font-bold ${getRemainingPostsToday() === 0 ? 'text-red-400' : getRemainingPostsToday() <= 2 ? 'text-yellow-400' : 'text-green-400'}`}>
                  متبقي لك {getRemainingPostsToday()} بوستات اليوم
                </span>
                {forumCooldownEnds && getRemainingPostsToday() === 0 && (
                  <span className="text-sm text-red-400 font-bold flex items-center gap-1">
                    <Clock size={14} />
                    يمكنك النشر مجدداً بعد {formatCooldownTime(forumCooldownEnds)}
                  </span>
                )}
              </div>

              <div className="mt-4 space-y-3">
                <textarea
                  value={forumContent}
                  onChange={(e) => setForumContent(e.target.value)}
                  placeholder={isBlockedFromForum ? 'أنت محظور من النشر في المنتدى' : 'اكتب هنا...'}
                  disabled={isBlockedFromForum || (getRemainingPostsToday() === 0)}
                  className={`w-full p-4 bg-white/5 border border-white/10 rounded-xl focus:border-brand-gold outline-none h-28 ${isBlockedFromForum || (getRemainingPostsToday() === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={async () => {
                      if (isBlockedFromForum) {
                        alert('أنت محظور من النشر في المنتدى');
                        return;
                      }
                      if (getRemainingPostsToday() === 0) {
                        alert('لقد تجاوزت الحد اليومي (5 بوستات). حاول مرة أخرى لاحقاً');
                        return;
                      }
                      if (!forumContent.trim()) return;
                      setForumLoading(true);
                      const res = await d1Api.createForumPost({ content: forumContent.trim(), user_id: user?.id });
                      setForumLoading(false);
                      if (res.success) {
                        setForumContent('');
                        alert('✅ تم إرسال البوست للمراجعة');
                        // Refresh my posts
                        const forumRes = await d1Api.getForumPostsForUser(user.id);
                        if (forumRes.success) setMyForumPosts(forumRes.data || []);
                      } else {
                        alert('❌ فشل الإرسال: ' + (res.error || 'Unknown'));
                      }
                    }}
                    disabled={forumLoading || isBlockedFromForum || (getRemainingPostsToday() === 0)}
                    className="btn-main px-6 py-3 rounded-xl font-bold disabled:opacity-50"
                  >
                    {forumLoading ? 'جارٍ الإرسال...' : 'إرسال'}
                  </button>
                  <button
                    onClick={async () => {
                      const res = await d1Api.getForumPosts('approved');
                      if (res.success) setForumPosts(res.data || []);
                      // Also refresh my posts
                      const forumRes = await d1Api.getForumPostsForUser(user.id);
                      if (forumRes.success) setMyForumPosts(forumRes.data || []);
                    }}
                    className="bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl font-bold transition"
                  >
                    تحديث
                  </button>
                </div>
              </div>
            </div>

            {/* My Posts with Delete buttons and Admin Reply */}
            {myForumPosts.filter(p => p.status === 'pending' || (p.status === 'approved' && p.user_id === user?.id)).length > 0 && (
              <div className="space-y-4">
                <h3 className="font-bold text-lg">📝 بوستاتك</h3>
                {myForumPosts.filter(p => p.user_id === user?.id).map(p => (
                  <div key={p.id} className={`glass-panel p-6 relative border-l-4 ${p.status === 'pending' ? 'border-yellow-500' : 'border-green-500'}`}>
                    {p.status === 'pending' && (
                      <span className="absolute top-4 left-4 bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        <Clock size={12} /> قيد المراجعة
                      </span>
                    )}
                    <button
                      onClick={() => handleDeleteForumPost(p.id)}
                      className="absolute top-4 right-4 text-red-400 hover:text-red-300 bg-red-400/10 p-2 rounded-lg transition"
                      title="حذف البوست"
                    >
                      <Trash2 size={16} />
                    </button>
                    <p className="mt-3 text-white whitespace-pre-wrap break-words pr-12">{p.content}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(p.created_at || Date.now()).toLocaleString('ar-EG')}
                    </p>
                    {/* Admin Reply Display */}
                    {p.admin_reply && (
                      <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                        <span className="text-xs font-bold text-blue-400 flex items-center gap-1">
                          <Award size={14} /> رد المستر:
                        </span>
                        <p className="text-slate-300 mt-2">{p.admin_reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg">🟢 البوستات المعتمدة</h3>
                <button
                  onClick={async () => {
                    const res = await d1Api.getForumPosts('approved');
                    if (res.success) setForumPosts(res.data || []);
                  }}
                  className="text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl font-bold transition"
                >
                  تحميل
                </button>
              </div>

              {forumPosts.length === 0 ? (
                <div className="glass-panel p-10 text-center text-slate-400">
                  لا توجد بوستات معتمدة حالياً.
                </div>
              ) : (
                forumPosts.map(p => {
                  const isOwnPost = p.user_id === user?.id;
                  return (
                    <div key={p.id} className="glass-panel p-6 relative">
                      {isOwnPost && (
                        <button
                          onClick={() => handleDeleteForumPost(p.id)}
                          className="absolute top-4 left-4 text-red-400 hover:text-red-300 bg-red-400/10 p-2 rounded-lg transition"
                          title="حذف البوست"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                      <div className="text-xs text-slate-400">
                        {p.firstName || 'طالب'} {p.lastName || ''} • {new Date(p.created_at || Date.now()).toLocaleString('ar-EG')}
                        {isOwnPost && <span className="ml-2 text-green-400">(بوستك)</span>}
                      </div>
                      <p className="mt-3 text-white whitespace-pre-wrap break-words">{p.content}</p>
                      {/* Admin Reply Display for all approved posts */}
                      {p.admin_reply && (
                        <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                          <span className="text-xs font-bold text-blue-400 flex items-center gap-1">
                            <Award size={14} /> رد المستر:
                          </span>
                          <p className="text-slate-300 mt-2">{p.admin_reply}</p>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'evaluation' && (
          <div className="space-y-6 animate-fade-in" dir="rtl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center shadow-lg">
                <Star size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">تقييمي الشامل 📊</h2>
                <p className="text-slate-400 text-sm">متابعة مستواك، درجاتك في الامتحانات، والنقاط التي جمعتها</p>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="glass-panel p-6 border-b-4 border-amber-500 text-center relative overflow-hidden">
                <div className="absolute -right-4 -top-4 opacity-10"><Award size={80} /></div>
                <p className="text-slate-400 font-bold mb-2">إجمالي النقاط</p>
                <p className="text-4xl font-black text-amber-400">{userData?.points || 0}</p>
                <p className="text-xs text-slate-500 mt-2">من مشاهدة المحاضرات وحل الامتحانات</p>
              </div>
              <div className="glass-panel p-6 border-b-4 border-blue-500 text-center relative overflow-hidden">
                <div className="absolute -right-4 -top-4 opacity-10"><Video size={80} /></div>
                <p className="text-slate-400 font-bold mb-2">المحاضرات المكتملة</p>
                <p className="text-4xl font-black text-blue-400">{completedLectures.length}</p>
                <p className="text-xs text-slate-500 mt-2">محاضرة تم مشاهدتها</p>
              </div>
              <div className="glass-panel p-6 border-b-4 border-green-500 text-center relative overflow-hidden">
                <div className="absolute -right-4 -top-4 opacity-10"><ClipboardList size={80} /></div>
                <p className="text-slate-400 font-bold mb-2">الامتحانات المحلولة</p>
                <p className="text-4xl font-black text-green-400">{myAttempts.length}</p>
                <p className="text-xs text-slate-500 mt-2">امتحان تم اجتيازه</p>
              </div>
            </div>

            {/* Quiz Grades Table */}
            <div className="glass-panel p-6 mt-8">
              <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
                <ClipboardList size={20} className="text-brand-gold" />
                سجل درجات الامتحانات
              </h3>
              
              {myAttempts.length === 0 ? (
                <div className="text-center py-10 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-slate-400">لم تقم بحل أي امتحانات حتى الآن.</p>
                  <p className="text-xs text-slate-500 mt-2">ابدأ بمشاهدة المحاضرات وحل الاختبارات لجمع النقاط!</p>
                </div>
              ) : (
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="w-full text-right">
                    <thead className="border-b border-white/10 bg-white/5">
                      <tr>
                        <th className="p-4 font-bold text-slate-300 rounded-tr-xl">اسم الامتحان</th>
                        <th className="p-4 font-bold text-slate-300 text-center">الدرجة</th>
                        <th className="p-4 font-bold text-slate-300 text-center">النسبة</th>
                        <th className="p-4 font-bold text-slate-300 text-left rounded-tl-xl">التاريخ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myAttempts.map((attempt) => {
                        const percentage = attempt.total > 0 ? Math.round((attempt.score / attempt.total) * 100) : 0;
                        let badgeColor = 'bg-red-500/20 text-red-400';
                        if (percentage >= 85) badgeColor = 'bg-green-500/20 text-green-400';
                        else if (percentage >= 65) badgeColor = 'bg-yellow-500/20 text-yellow-400';

                        return (
                          <tr key={attempt.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                            <td className="p-4 font-bold text-white">{attempt.quiz_title || 'امتحان بدون اسم'}</td>
                            <td className="p-4 text-center font-black">
                              <span className="text-amber-400">{attempt.score}</span> 
                              <span className="text-slate-500 mx-1">/</span> 
                              <span className="text-slate-400">{attempt.total}</span>
                            </td>
                            <td className="p-4 text-center">
                              <span className={`px-3 py-1 rounded-lg font-bold text-xs ${badgeColor}`}>
                                {percentage}%
                              </span>
                            </td>
                            <td className="p-4 text-left text-sm text-slate-400">
                              {attempt.created_at ? new Date(Number(attempt.created_at) || attempt.created_at).toLocaleDateString('ar-EG') : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}


        {activeTab === 'leaderboard' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 text-yellow-500 flex items-center justify-center"><Award size={24} /></div>
              <div><h2 className="text-2xl font-bold">لوحة الشرف 🏆</h2><p className="text-slate-400 text-sm">أوائل منصة سبيل التفوق بناءً على نقاط الامتحانات</p></div>
            </div>

            {/* Fix 4: Grade Filter Tabs */}
            <div className="flex gap-2 flex-wrap">
              {[
                { value: '', label: 'الكل' },
                { value: '1sec', label: 'أولى ثانوي' },
                { value: '2sec', label: 'تانية ثانوي' },
                { value: '3sec', label: 'تالتة ثانوي' }
              ].map(grade => (
                <button
                  key={grade.value}
                  onClick={async () => {
                    setLeaderboardFilter(grade.value);
                    const res = await d1Api.getLeaderboard(grade.value || undefined);
                    if (res.success) setLeaderboard(res.data || []);
                  }}
                  className={`px-4 py-2 rounded-xl font-bold text-sm transition ${
                    leaderboardFilter === grade.value
                      ? 'bg-gradient-to-r from-brand-red to-brand-gold text-white'
                      : 'bg-white/10 text-slate-400 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {grade.label}
                </button>
              ))}
            </div>

            <div className="glass-panel p-6">
              {leaderboard.length === 0 ? (
                <p className="text-center text-slate-400 py-8">لا يوجد طلاب في لوحة الشرف حتى الآن. كن أول المتفوقين!</p>
              ) : (
                <div className="space-y-4">
                  {leaderboard.map((student, index) => (
                    <div key={student.id} className={`flex items-center justify-between p-4 rounded-xl border ${index === 0 ? 'bg-yellow-500/10 border-yellow-500/30' : index === 1 ? 'bg-slate-300/10 border-slate-300/30' : index === 2 ? 'bg-amber-700/10 border-amber-700/30' : 'bg-white/5 border-white/10'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg ${index === 0 ? 'bg-yellow-500 text-black' : index === 1 ? 'bg-slate-300 text-black' : index === 2 ? 'bg-amber-600 text-white' : 'bg-white/10 text-white'}`}>#{index + 1}</div>
                        <div>
                          <h4 className="font-bold text-lg text-white">{[student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ')}</h4>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-slate-400">{student.governorate || 'طالب متميز'}</p>
                            {student.grade && (
                              <span className="text-xs bg-white/10 text-slate-300 px-2 py-0.5 rounded-full">
                                {student.grade === '1sec' ? 'أولى ثانوي' : student.grade === '2sec' ? 'تانية ثانوي' : student.grade === '3sec' ? 'تالتة ثانوي' : 'غير محدد'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="bg-brand-gold text-white px-4 py-2 rounded-lg font-black">{student.points} نقطة</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        {showCodeModal && (
          <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={() => setShowCodeModal(false)}>
            <div className="glass-panel p-8 max-w-md w-full animate-scale-in relative" onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowCodeModal(false)} className="absolute top-4 left-4 p-2 hover:bg-white/10 rounded-full">
                <span className="text-gray-400 text-xl font-bold">&times;</span>
              </button>

              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">أدخل كود السنتر</h3>
                <p className="text-slate-400 text-sm mt-2">إذا كان لديك كود خصم من السنتر، أدخله هنا</p>
              </div>

              <div className="space-y-4">
                <input
                  type="text"
                  value={centerCode}
                  onChange={(e) => setCenterCode(e.target.value.toUpperCase())}
                  placeholder="مثال: C123ABC456"
                  className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-center text-2xl tracking-widest text-white"
                  maxLength={12}
                  autoFocus
                />

                {codeError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                    <p className="text-red-400 text-sm text-center">{codeError}</p>
                  </div>
                )}

                {codeSuccess && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3">
                    <p className="text-green-400 text-sm text-center">{codeSuccess}</p>
                  </div>
                )}

                <button
                  onClick={handleCodeSubmit}
                  disabled={codeValidating}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 rounded-xl font-bold disabled:opacity-50"
                >
                  {codeValidating ? 'جاري التحقق...' : 'تفعيل الكود'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showPurchaseModal && selectedCourse && (
          <CoursePurchaseModal
            course={selectedCourse}
            onClose={() => {
              setShowPurchaseModal(false);
              setSelectedCourse(null);
            }}
            user={user}
            userData={userData}
            onSuccess={() => {
              setShowPurchaseModal(false);
              setSelectedCourse(null);
              window.location.reload();
            }}
          />
        )}
      </DashboardLayout>

      {showQuizConfirm && (
        <div className="fixed inset-0 bg-black/95 z-[150] flex items-center justify-center p-4 backdrop-blur-md" onClick={() => setShowQuizConfirm(false)}>
          <div className="glass-panel p-8 max-w-md w-full animate-scale-in relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowQuizConfirm(false)} className="absolute top-4 left-4 p-2 hover:bg-white/10 rounded-full transition">
              <X size={24} className="text-slate-400" />
            </button>

            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">هل أنت مستعد لبدء الاختبار؟</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                ⏱️ تأكد من أن لديك الوقت الكافي لإكمال الاختبار.
                <br />
                📝 لن تتمكن من العودة للفيديو أثناء الاختبار.
                <br />
                🎯 ركز جيداً وحاول الحصول على أعلى درجة.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleStartQuiz}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-green-600/20"
              >
                <ClipboardList size={20} />
                نعم، أنا مستعد
              </button>
              <button
                onClick={() => setShowQuizConfirm(false)}
                className="w-full bg-white/5 hover:bg-white/10 text-slate-300 py-4 rounded-xl font-bold transition-all duration-200"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {showQuiz && currentQuiz && (
        <div className="fixed inset-0 z-[200] bg-black overflow-y-auto">
          <div className="max-w-4xl mx-auto p-4 md:p-8">
            {/* Close button */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => {
                  setShowQuiz(false);
                  setCurrentQuiz(null);
                  setQuizQuestions([]);
                }}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold text-sm transition"
              >
                ✕ إغلاق
              </button>
            </div>

            {quizQuestionsLoading ? (
              <div className="glass-panel p-16 text-center">
                <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-400 text-lg">جاري تحميل أسئلة الامتحان...</p>
              </div>
            ) : quizQuestions.length === 0 ? (
              <div className="glass-panel p-16 text-center">
                <p className="text-slate-400 text-lg">لا توجد أسئلة في هذا الامتحان بعد</p>
                <button
                  onClick={() => {
                    setShowQuiz(false);
                    setCurrentQuiz(null);
                  }}
                  className="mt-6 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition"
                >
                  العودة
                </button>
              </div>
            ) : (
              <QuizPlayer
                quiz={currentQuiz}
                questions={quizQuestions}
                userId={user.id}
                readOnly={quizReadOnly}
                previousAnswers={quizPreviousAnswers}
                previousScore={quizPreviousScore}
                onFinish={(result) => {
                  setShowQuiz(false);
                  setCurrentQuiz(null);
                  setQuizQuestions([]);
                  setQuizReadOnly(false);
                  
                  if (!quizReadOnly && result && user?.id) {
                    // Skip saving for custom mistakes quiz (practice only)
                    if (currentQuiz?.id === 'custom-mistakes') {
                      // Don't save attempt — just close
                      return;
                    }
                    if (currentQuiz?.isCombo) {
                      // Split combined answers back to their individual quizzes
                      const attemptsByQuiz = {};
                      result.answers.forEach((ans, idx) => {
                         const qid = quizQuestions[idx].original_quiz_id;
                         if (!attemptsByQuiz[qid]) attemptsByQuiz[qid] = { score: 0, total: 0, answers: [] };
                         attemptsByQuiz[qid].answers.push(ans);
                         attemptsByQuiz[qid].total += 1;
                         if (ans === quizQuestions[idx].correct_answer) attemptsByQuiz[qid].score += 1;
                      });
                      
                      // Save all attempts simultaneously
                      Promise.all(Object.entries(attemptsByQuiz).map(([qid, data]) => {
                         return d1Api.saveQuizAttempt({
                            user_id: user.id, quiz_id: qid, score: data.score, total: data.total, answers: data.answers
                         }).then(res => {
                            if (res.success) {
                               setMyAttempts(prev => [...prev, {
                                  id: res.data?.id || Date.now().toString(), quiz_id: qid, score: data.score, total: data.total, created_at: Date.now()
                               }]);
                            }
                         });
                      })).then(() => {
                         d1Api.getWrongAnswers(user.id).then(res => { if (res.success) setWrongAnswers(res.data || []); });
                      });
                    } else {
                      // Normal single quiz save
                      d1Api.saveQuizAttempt({
                        user_id: user.id, quiz_id: currentQuiz.id, score: result.score, total: result.total, answers: result.answers
                      }).then(res => {
                        if (res.success) {
                          setMyAttempts(prev => [...prev, {
                            id: res.data?.id || Date.now().toString(), quiz_id: currentQuiz.id, score: result.score, total: result.total, created_at: Date.now()
                          }]);
                        }
                      }).catch(err => console.error('Failed to save attempt:', err));
                      d1Api.getWrongAnswers(user.id).then(res => { if (res.success) setWrongAnswers(res.data || []); });
                    }
                  }
                }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};